import { useCallback, useEffect, useRef, useState } from "react";

type RemoteNode = { id: string };

/** How far an arriving card rises as it fades in, in px: the prototype's arrival. */
export const ARRIVAL_RISE = 6;

/**
 * The nodes in `next` that were not in `previous`.
 * With no `previous`, `next` is the canvas as first loaded, so nothing in it arrived.
 */
export function diffArrivals(
  previous: ReadonlySet<string> | undefined,
  next: RemoteNode[],
): string[] {
  if (!previous) return [];
  return next.filter((node) => !previous.has(node.id)).map((node) => node.id);
}

type Rect = { id: string; x: number; y: number; width: number; height: number };
export type ArrivalSide = "above" | "below" | "left" | "right";

/** The share of an arrival's area, or of the view when the arrival is larger, that must be in view before it counts as seen. */
export const SEEN_AREA_SHARE = 0.3;

const SIDE_WORDS: Record<ArrivalSide, string> = {
  above: "above",
  below: "below",
  left: "to the left",
  right: "to the right",
};

/**
 * Sorts arrivals by where they sit on screen: one with enough of its area inside the safe area, below the
 * `safeTop` the header covers, is seen; the rest are off screen. `side` is the edge they all lie past, or null
 * when they lie past different edges.
 */
export function placeArrivals(
  rects: Rect[],
  viewport: { x: number; y: number; zoom: number },
  size: { width: number; height: number },
  safeTop = 0,
) {
  const seen: string[] = [];
  const offscreen: string[] = [];
  const sides = new Set<ArrivalSide>();
  const halfWidth = size.width / 2;
  const halfHeight = (size.height - safeTop) / 2;
  const viewArea = size.width * (size.height - safeTop);
  for (const rect of rects) {
    const left = rect.x * viewport.zoom + viewport.x;
    const top = rect.y * viewport.zoom + viewport.y;
    const width = rect.width * viewport.zoom;
    const height = rect.height * viewport.zoom;
    const visibleWidth = Math.max(
      0,
      Math.min(left + width, size.width) - Math.max(left, 0),
    );
    const visibleHeight = Math.max(
      0,
      Math.min(top + height, size.height) - Math.max(top, safeTop),
    );
    if (
      visibleWidth * visibleHeight >=
      Math.min(width * height, viewArea) * SEEN_AREA_SHARE
    ) {
      seen.push(rect.id);
      continue;
    }
    offscreen.push(rect.id);
    const offsetX = left + width / 2 - halfWidth;
    const offsetY = top + height / 2 - (safeTop + halfHeight);
    sides.add(
      Math.abs(offsetY) / halfHeight >= Math.abs(offsetX) / halfWidth
        ? offsetY > 0
          ? "below"
          : "above"
        : offsetX > 0
          ? "right"
          : "left",
    );
  }
  const side: ArrivalSide | null =
    sides.size === 1 ? ([...sides][0] ?? null) : null;
  return { seen, offscreen, side };
}

const withSide = (text: string, side: ArrivalSide | null) =>
  side ? `${text} ${SIDE_WORDS[side]}` : text;

export const newPillLabel = (count: number, side: ArrivalSide | null) =>
  withSide(`${count} new`, side);

export const newPillAriaLabel = (count: number, side: ArrivalSide | null) =>
  withSide(`Show ${count} new ${count === 1 ? "item" : "items"}`, side);

/**
 * Items new to the canvas since it loaded, from the agent, a teammate or the person here. A node built for one
 * is marked with `isArriving` so its card fades in; each stays in `unseen` until the person has seen it or framed it.
 */
export function useCanvasArrivals({
  remote,
}: {
  remote: RemoteNode[] | undefined;
}) {
  const known = useRef<Set<string>>();
  // Only the latest result's arrivals: a card mounts within a poll of landing, so older ones need not enter again.
  const arriving = useRef(new Set<string>());
  const [unseen, setUnseen] = useState<string[]>([]);

  useEffect(() => {
    if (!remote) return;
    const arrived = diffArrivals(known.current, remote);
    const ids = new Set(remote.map((node) => node.id));
    known.current = ids;
    arriving.current = new Set(arrived);
    // An arrival removed before anyone saw it stops counting.
    setUnseen((current) => {
      const next = [...new Set([...current, ...arrived])].filter((id) =>
        ids.has(id),
      );
      return next.length === current.length && arrived.length === 0
        ? current
        : next;
    });
  }, [remote]);

  const isArriving = useCallback((id: string) => arriving.current.has(id), []);
  const dismiss = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setUnseen((current) => current.filter((id) => !ids.includes(id)));
  }, []);

  return { unseen, dismiss, isArriving };
}
